import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CURRENCIES,
  type Currency,
  loadCurrency,
  saveCurrency,
} from "../lib/currency";
import { CurrencyPicker } from "./CurrencyPicker";
import { useT, useLp, useLang } from "../lib/i18n";
import { useCloudPricingCopy } from "../lib/cloud-pricing";
import { useSession } from "../lib/auth";
import {
  createCheckoutSession,
  createPortalSession,
  getFounding100Remaining,
  getEntitlement,
  type CheckoutCycle,
  type Entitlement,
} from "../lib/api";
import {
  currentSourcePage,
  trackCheckoutStartedBeforeRedirect,
  trackPricingCtaClick,
  trackSignupClick,
} from "../lib/plausible";
import { attributionProps, getAttribution } from "../lib/attribution";

/** Shape of the Pro CTA right now — derived from session + entitlement. */
type ProCtaMode =
  | { kind: "signup" } // not signed in — send to /signup?plan=pro
  | { kind: "checkout" } // signed in, free → open Stripe Checkout
  | { kind: "manage" }; // signed in, pro → open Stripe Customer Portal

/**
 * Founding-100 copy is language-only — the actual price string comes from the
 * selected currency (see `currencyInfo.foundingPriceFormatted` below). Stripe
 * always charges in BRL; USD/EUR are display-only display values close to FX.
 */
const foundingCopy = {
  en: {
    cycle: "Founding 100",
    checking: "Checking Founding-100 spots...",
    left: (n: number) => `${n} Founding-100 spots left`,
    soldOut: "Founding-100 sold out",
  },
  pt: {
    cycle: "Founding 100",
    checking: "Checando vagas Founding-100...",
    left: (n: number) => `${n} vagas Founding-100 restantes`,
    soldOut: "Founding-100 esgotado",
  },
  es: {
    cycle: "Founding 100",
    checking: "Comprobando cupos Founding-100...",
    left: (n: number) => `${n} cupos Founding-100 disponibles`,
    soldOut: "Founding-100 agotado",
  },
} as const;

export function Pricing() {
  const t = useT();
  const lp = useLp();
  const lang = useLang();
  const navigate = useNavigate();
  const session = useSession();
  const cloudPricing = useCloudPricingCopy();
  const pricing = cloudPricing?.section ?? t.pricing;
  const { free, pro } = pricing;

  // Default currency is country-based (Brazil → BRL, everyone else → USD).
  // A stored selector choice always wins, and language changes do not reset it.
  const [currency, setCurrency] = useState<Currency>(() => loadCurrency());
  useEffect(() => setCurrency(loadCurrency()), []);

  const handleCurrencyChange = (next: Currency) => {
    setCurrency(next);
    saveCurrency(next);
  };

  const [cycle, setCycle] = useState<CheckoutCycle>("founding100");
  const [foundingRemaining, setFoundingRemaining] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    getFounding100Remaining()
      .then(({ remaining }) => {
        if (cancelled) return;
        const safeRemaining = Math.max(0, Math.min(100, remaining));
        setFoundingRemaining(safeRemaining);
        if (safeRemaining <= 0) {
          setCycle((current) => current === "founding100" ? "annual" : current);
        }
      })
      .catch(() => {
        if (!cancelled) setFoundingRemaining(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currencyInfo = (cloudPricing?.currencies ?? CURRENCIES)[currency];
  const founding = foundingCopy[lang];
  const foundingSoldOut = foundingRemaining !== null && foundingRemaining <= 0;
  const proPrice = cycle === "founding100"
    ? currencyInfo.foundingPriceFormatted
    : cycle === "annual"
      ? currencyInfo.proAnnualMonthlyFormatted
      : currencyInfo.proFormatted;
  // Founding 100 badge and note are currency-aware when the cloud-pricing
  // copy is active (the new pricing world). Fall back to the static
  // translation strings when the cloud flag is off.
  const proBadge = cloudPricing?.founding
    ? cloudPricing.founding.badge(currencyInfo.foundingPriceFormatted)
    : pro.badge;
  const proNote = cloudPricing?.founding
    ? cloudPricing.founding.note(currencyInfo.foundingPriceFormatted)
    : pro.note;
  const foundingStatus = foundingRemaining === null
    ? founding.checking
    : foundingSoldOut
      ? founding.soldOut
      : founding.left(foundingRemaining);

  // Entitlement drives whether the Pro CTA subscribes or manages. Only
  // fetched when a session exists — anonymous visitors get the signup path
  // without a round-trip.
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  useEffect(() => {
    if (!session) {
      setEntitlement(null);
      return;
    }
    let cancelled = false;
    getEntitlement(session.tokens.access_token)
      .then((e) => {
        if (!cancelled) setEntitlement(e);
      })
      .catch(() => {
        // Entitlement endpoint may be disabled (dev without Stripe wired)
        // or transiently failing — fall through to the safe default
        // (signup / checkout).
        if (!cancelled) setEntitlement(null);
      });
    return () => {
      cancelled = true;
    };
  }, [session]);

  const ctaMode: ProCtaMode = !session
    ? { kind: "signup" }
    : entitlement?.plan === "pro"
      ? { kind: "manage" }
      : { kind: "checkout" };

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleProClick = async () => {
    if (busy) return;
    setError(null);
    const funnelProps = {
      source_page: currentSourcePage(),
      lang,
      plan: "pro",
      billing_cycle: cycle,
    };
    trackPricingCtaClick(funnelProps);

    if (ctaMode.kind === "signup") {
      navigate(`${lp}/?auth=signup&plan=pro&cycle=${cycle}`);
      return;
    }
    if (!session) {
      navigate(`${lp}/?auth=signup&plan=pro&cycle=${cycle}`);
      return;
    }

    setBusy(true);
    try {
      const attr = getAttribution();
      const res = ctaMode.kind === "manage"
        ? await createPortalSession(session.tokens.access_token)
        : await createCheckoutSession(session.tokens.access_token, { cycle, ...attr });
      // Redirect in the same tab — users expect the Stripe flow to replace
      // the current page, and popup blockers kill window.open flows.
      if (ctaMode.kind === "checkout") {
        trackCheckoutStartedBeforeRedirect(
          () => window.location.assign(res.url),
          { ...attributionProps(), ...funnelProps },
        );
        return;
      }

      window.location.assign(res.url);
    } catch {
      setError(pro.error);
      setBusy(false);
    }
  };

  const proCtaLabel =
    busy
      ? pro.ctaLoading
      : ctaMode.kind === "manage"
        ? pro.ctaManage
        : pro.cta;

  return (
    <section id="pricing" className="border-t border-margin dark:border-paper/10">
      <div className="mx-auto max-w-6xl px-6 py-24">
        <div className="flex items-end justify-between flex-wrap gap-6 max-w-3xl">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-ink/50 dark:text-paper/50">
              {pricing.label}
            </p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
              {pricing.heading}
            </h2>
            <p className="mt-4 text-ink/70 dark:text-paper/70 leading-relaxed">
              {pricing.description}
            </p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {/* Billing cycle toggle */}
            <div
              role="group"
              aria-label="Billing cycle"
              className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-2xl border border-ink/15 dark:border-paper/15 p-1 text-xs font-mono"
            >
              <button
                type="button"
                onClick={() => setCycle("founding100")}
                disabled={foundingSoldOut}
                className={[
                  "rounded-full px-4 py-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-45",
                  cycle === "founding100"
                    ? "bg-graphite text-paper"
                    : "text-ink/60 dark:text-paper/60 hover:text-ink dark:hover:text-paper",
                ].join(" ")}
              >
                {founding.cycle}
              </button>
              <button
                type="button"
                onClick={() => setCycle("monthly")}
                className={[
                  "rounded-full px-4 py-1.5 transition-colors",
                  cycle === "monthly"
                    ? "bg-graphite text-paper"
                    : "text-ink/60 dark:text-paper/60 hover:text-ink dark:hover:text-paper",
                ].join(" ")}
              >
                {pro.cycleMonthly}
              </button>
              <button
                type="button"
                onClick={() => setCycle("annual")}
                className={[
                  "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors",
                  cycle === "annual"
                    ? "bg-graphite text-paper"
                    : "text-ink/60 dark:text-paper/60 hover:text-ink dark:hover:text-paper",
                ].join(" ")}
              >
                {pro.cycleAnnual}
                <span
                  className={[
                    "font-mono text-[10px] tracking-tight",
                    cycle === "annual" ? "text-paper/70" : "text-graphite dark:text-graphite-muted",
                  ].join(" ")}
                >
                  {pro.savingsBadge(currencyInfo.proAnnualSavingsPct)}
                </span>
              </button>
            </div>
            <CurrencyPicker value={currency} onChange={handleCurrencyChange} />
          </div>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {/* Free */}
          <article className="rounded-2xl border border-margin dark:border-paper/10 p-8 flex flex-col">
            <header>
              <div className="font-mono text-xs uppercase tracking-wider text-ink/50 dark:text-paper/50">
                {free.label}
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-5xl font-semibold tracking-tight text-ink dark:text-paper">
                  0
                </span>
                <span className="text-ink/60 dark:text-paper/60 text-sm">
                  {free.period}
                </span>
              </div>
              <p className="mt-3 text-sm text-ink/60 dark:text-paper/60">
                {free.tagline}
              </p>
            </header>

            <ul className="mt-8 space-y-3 text-sm text-ink/80 dark:text-paper/80 flex-1">
              {free.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Link
              to={lp + "/?auth=signup&plan=free"}
              onClick={() => trackSignupClick({
                source_page: currentSourcePage(),
                lang,
                plan: "free",
              })}
              className="mt-8 inline-flex items-center justify-center rounded-full border border-ink/20 dark:border-paper/30 px-5 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
            >
              {free.cta}
            </Link>
          </article>

          {/* Pro */}
          <article className="relative rounded-2xl border-2 border-graphite dark:border-graphite-muted p-8 flex flex-col bg-graphite/[0.02] dark:bg-graphite/[0.08]">
            <span className="absolute -top-3 left-8 inline-flex items-center rounded-full bg-graphite text-paper text-[10px] font-mono uppercase tracking-wider px-3 py-1">
              {proBadge}
            </span>

            <header>
              <div className="font-mono text-xs uppercase tracking-wider text-ink/50 dark:text-paper/50">
                {pro.label}
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-5xl font-semibold tracking-tight text-ink dark:text-paper">
                  {proPrice}
                </span>
                <span className="text-ink/60 dark:text-paper/60 text-sm">
                  {pro.period}
                </span>
              </div>
              {cycle === "annual" && (
                <p className="mt-1 text-xs text-ink/50 dark:text-paper/50">
                  {pro.annualBilledAs(
                    currencyInfo.proAnnualTotalFormatted,
                    currencyInfo.proAnnualSavingsPct,
                  )}
                </p>
              )}
              <p
                className={[
                  "mt-1 text-xs font-mono",
                  foundingSoldOut
                    ? "text-ink/45 dark:text-paper/45"
                    : "text-graphite dark:text-graphite-muted",
                ].join(" ")}
              >
                {foundingStatus}
              </p>
              <p className="mt-3 text-sm text-ink/60 dark:text-paper/60">
                {pro.tagline}
              </p>
            </header>

            <ul className="mt-8 space-y-3 text-sm text-ink/80 dark:text-paper/80 flex-1">
              {pro.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={handleProClick}
              disabled={busy}
              className="mt-8 inline-flex items-center justify-center rounded-full bg-graphite text-paper hover:bg-graphite-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-5 py-3 text-sm font-medium"
            >
              {proCtaLabel}
            </button>

            {error && (
              <p className="mt-3 text-center text-xs text-rec">{error}</p>
            )}

            <p className="mt-3 text-center text-[11px] text-ink/50 dark:text-paper/50">
              {proNote}
            </p>
          </article>
        </div>

        <p className="mt-10 text-center text-xs text-ink/50 dark:text-paper/50">
          {pricing.footer}
        </p>
      </div>
    </section>
  );
}

function Check() {
  return (
    <svg
      className="mt-0.5 h-4 w-4 flex-shrink-0 text-graphite dark:text-graphite-muted"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3,8 7,12 13,4" />
    </svg>
  );
}
